import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { customFieldsCopy } from "../../taxonomy/copy";
import TaxonomyFieldOverlay, { FormState } from "../../taxonomy/TaxonomyFieldOverlay";
import ProcessFieldMappingEditor from "../../taxonomy/ProcessFieldMappingEditor";
import {
  listOrganizationTaxonomy,
  listTaxonomyDefinitions,
  removeOrganizationTaxonomy,
  upsertOrganizationTaxonomy,
  upsertTaxonomyDefinition,
} from "../../taxonomy/taxonomyApi";
import type { ResolvedFieldConfig, TaxonomyDefinition, TaxonomyUsedIn } from "../../taxonomy/types";
import { fieldTypeLabel, isSelectType, normalizeTaxonomyName, trimTaxonomyInput, usedInLabel } from "../../taxonomy/values";

type Tab = "mine" | "browse";
type UsedFilter = "" | TaxonomyUsedIn;
type SourceFilter = "" | "GLOBAL" | "STUDIO";

const CustomFields = () => {
  const [tab, setTab] = useState<Tab>("mine");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mine, setMine] = useState<ResolvedFieldConfig[]>([]);
  const [browse, setBrowse] = useState<TaxonomyDefinition[]>([]);
  const [search, setSearch] = useState("");
  const [usedIn, setUsedIn] = useState<UsedFilter>("");
  const [source, setSource] = useState<SourceFilter>("");
  const [overlay, setOverlay] = useState<{ open: boolean; mode: "add" | "edit"; initial?: Partial<FormState> }>({
    open: false,
    mode: "add",
  });
  const [overlayError, setOverlayError] = useState("");
  const [mappingFieldId, setMappingFieldId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const [orgRes, listRes] = await Promise.all([
      listOrganizationTaxonomy(),
      listTaxonomyDefinitions(),
    ]);
    if (!orgRes.success) {
      setError(orgRes.message || "Unable to load custom fields.");
    } else {
      setMine(orgRes.data || []);
    }
    if (listRes.success) {
      setBrowse(listRes.data || []);
    }
    if (!listRes.success) setError(listRes.message || "Unable to browse custom fields. Please retry.");
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mineVisible = useMemo(() => {
    const q = normalizeTaxonomyName(search);
    return mine.filter((row) => {
      if (usedIn && row.usedIn !== usedIn) return false;
      if (source && row.source !== source) return false;
      if (!q) return true;
      return normalizeTaxonomyName(`${row.name} ${row.description || ""}`).includes(q);
    });
  }, [mine, search, usedIn, source]);

  const browseVisible = useMemo(() => {
    const q = normalizeTaxonomyName(search);
    return browse.filter((row) => {
      if (usedIn && row.usedIn !== usedIn) return false;
      if (source && row.source !== source) return false;
      if (!q) return true;
      return normalizeTaxonomyName(`${row.name} ${row.description || ""}`).includes(q);
    });
  }, [browse, search, usedIn, source]);

  const enabledIds = useMemo(() => new Set(mine.filter((row) => row.enabled).map((row) => row.taxonomyId)), [mine]);

  const openAdd = (name = "") => {
    setOverlayError("");
    setOverlay({
      open: true,
      mode: "add",
      initial: { name, source: "STUDIO" },
    });
  };

  const openEdit = (row: ResolvedFieldConfig) => {
    const def = browse.find((item) => item.id === row.taxonomyId);
    setOverlayError("");
    setOverlay({
      open: true,
      mode: "edit",
      initial: {
        id: row.taxonomyId,
        name: row.name,
        description: row.description || def?.description || "",
        fieldType: row.fieldType,
        usedIn: row.usedIn,
        options: (def?.options || row.options || []).map((opt) => ({
          id: opt.id,
          label: opt.label,
          active: opt.active,
        })),
        unit: row.unit || "",
        tags: (def?.tags || []).join(", "),
        requiredOnPublish: row.requiredOnPublish,
        visibility: row.visibility || "INTERNAL",
        source: row.source,
      },
    });
  };

  const handleSave = async (form: FormState) => {
    setSaving(true);
    setOverlayError("");
    const tags = form.tags
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
    if (overlay.mode === "edit" && form.source === "GLOBAL" && form.id) {
      const current = mine.find((row) => row.taxonomyId === form.id);
      const res = await upsertOrganizationTaxonomy({
        taxonomyId: form.id,
        enabled: current?.enabled !== false,
        requiredOnPublish: form.requiredOnPublish,
        displayOrder: current?.displayOrder || 0,
        visibility: form.visibility,
      });
      setSaving(false);
      if (!res.success) {
        setOverlayError(res.message || "Unable to save field.");
        return;
      }
      notifications({ title: "Success!", message: res.message || "Field saved", type: "success" });
      setOverlay({ open: false, mode: "add" });
      load();
      return;
    }
    const res = await upsertTaxonomyDefinition({
      id: form.id || null,
      name: trimTaxonomyInput(form.name),
      description: form.description.trim(),
      fieldType: form.fieldType,
      usedIn: form.usedIn,
      options: isSelectType(form.fieldType)
        ? form.options.map((option) => ({ ...option, label: trimTaxonomyInput(option.label) })).filter((option) => option.label)
        : [],
      unit: form.unit || null,
      tags,
    });
    if (!res.success) {
      setSaving(false);
      setOverlayError(res.message || "Unable to save field.");
      // Refresh matches after a concurrent normalized duplicate is reported.
      load();
      return;
    }
    if (!res.data?.id) {
      setSaving(false);
      setOverlayError("The server did not return the saved field. Reload fields before retrying.");
      return;
    }
    if (res.data.id) {
      const current = mine.find((row) => row.taxonomyId === res.data?.id);
      const configRes = await upsertOrganizationTaxonomy({
        taxonomyId: res.data.id,
        enabled: current?.enabled !== false,
        requiredOnPublish: form.requiredOnPublish,
        displayOrder: current?.displayOrder ?? mine.length + 1,
        visibility: form.visibility,
      });
      if (!configRes.success) {
        setOverlay({ open: true, mode: "edit", initial: {
          ...form, id: res.data.id, options: res.data.options || [], source: res.data.source,
        } });
        setOverlayError("The field was saved, but its studio settings were not. Review Required on publish and retry Save Field.");
        setSaving(false);
        load();
        return;
      }
    }
    setSaving(false);
    notifications({ title: "Success!", message: res.message || "Field saved", type: "success" });
    setOverlay({ open: false, mode: "add" });
    load();
  };

  const toggleOrg = async (row: ResolvedFieldConfig, patch: Partial<ResolvedFieldConfig>) => {
    const res = await upsertOrganizationTaxonomy({
      taxonomyId: row.taxonomyId,
      enabled: patch.enabled ?? row.enabled,
      requiredOnPublish: patch.requiredOnPublish ?? row.requiredOnPublish,
      displayOrder: row.displayOrder,
      visibility: patch.visibility || row.visibility || "INTERNAL",
    });
    if (!res.success) {
      notifications({ title: "Error!", message: res.message || "Unable to update field", type: "danger" });
      return;
    }
    load();
  };

  const removeFromFuture = async (row: ResolvedFieldConfig) => {
    const ok = window.confirm("Remove this field from future use? Historical Product and Process values will be preserved. You can re-enable it later.");
    if (!ok) return;
    const res = await removeOrganizationTaxonomy(row.taxonomyId);
    if (!res.success) {
      notifications({ title: "Error!", message: res.message || "Unable to remove field", type: "danger" });
      return;
    }
    notifications({ title: "Success!", message: res.message || "Removed from future use", type: "success" });
    load();
  };

  const addToStudio = async (def: TaxonomyDefinition) => {
    const current = mine.find((row) => row.taxonomyId === def.id);
    const res = await upsertOrganizationTaxonomy({
      taxonomyId: def.id,
      enabled: true,
      requiredOnPublish: current?.requiredOnPublish ?? false,
      displayOrder: current?.displayOrder ?? mine.length + 1,
      visibility: current?.visibility || "INTERNAL",
    });
    if (!res.success) {
      notifications({ title: "Error!", message: res.message || "Unable to add field", type: "danger" });
      return false;
    }
    notifications({ title: "Success!", message: "Added to studio", type: "success" });
    setTab("mine");
    load();
    return true;
  };

  const useExisting = async (def: TaxonomyDefinition) => {
    setSaving(true);
    const added = await addToStudio(def);
    setSaving(false);
    if (added) setOverlay({ open: false, mode: "add" });
    else setOverlayError("Unable to add this field to your studio. Please retry.");
  };

  return (
    <div className="userMainDiv hkp-custom-fields">
      <Card className="createProductCard p-3">
        <div className="hkp-cf-head">
          <div>
            <h4 className="mb-2">{customFieldsCopy.pageTitle}</h4>
            <p className="text-muted mb-0">{customFieldsCopy.pageSubtitle}</p>
          </div>
          <Button color="primary" size="sm" onClick={() => openAdd()}>
            {customFieldsCopy.addField}
          </Button>
        </div>
        <div className="hkp-cf-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "mine"} className={tab === "mine" ? "is-active" : ""} onClick={() => setTab("mine")}>
            {customFieldsCopy.myFields}
          </button>
          <button type="button" role="tab" aria-selected={tab === "browse"} className={tab === "browse" ? "is-active" : ""} onClick={() => setTab("browse")}>
            {customFieldsCopy.browseFields}
          </button>
        </div>
        <CardBody className="p-0">
          <Row className="g-2 mb-3">
            <Col xs={12} md={4}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={customFieldsCopy.searchFields}
                aria-label={customFieldsCopy.searchFields}
              />
            </Col>
            <Col xs={6} md={4}>
              <select
                className="form-select"
                value={usedIn}
                onChange={(e) => setUsedIn(e.target.value as UsedFilter)}
                aria-label={customFieldsCopy.usedIn}
              >
                <option value="">{customFieldsCopy.filterAll}</option>
                <option value="PRODUCT">{customFieldsCopy.filterProduct}</option>
                <option value="PROCESS">{customFieldsCopy.filterProcess}</option>
                <option value="BOTH">{customFieldsCopy.filterBoth}</option>
              </select>
            </Col>
            <Col xs={6} md={4}>
              <select
                className="form-select"
                value={source}
                onChange={(e) => setSource(e.target.value as SourceFilter)}
                aria-label={customFieldsCopy.source}
              >
                <option value="">{customFieldsCopy.filterSourceAll}</option>
                <option value="GLOBAL">{customFieldsCopy.filterSourceGlobal}</option>
                <option value="STUDIO">{customFieldsCopy.filterSourceStudio}</option>
              </select>
            </Col>
          </Row>

          {error ? <p className="RequiredField">{error}</p> : null}

          {tab === "mine" ? (
            loading ? (
              <p className="text-muted">Loading custom fields…</p>
            ) : mineVisible.length === 0 ? (
              <div className="hkp-cf-empty">
                <p>{search ? customFieldsCopy.noSearchResults : customFieldsCopy.emptyMyFields}</p>
                {search ? (
                  <Button color="primary" size="sm" onClick={() => openAdd(search)}>
                    {customFieldsCopy.createNewField}
                  </Button>
                ) : (
                  <Button color="primary" size="sm" onClick={() => setTab("browse")}>
                    {customFieldsCopy.browseFieldsCta}
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="table-responsive hkp-cf-table">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>{customFieldsCopy.field}</th>
                        <th>{customFieldsCopy.type}</th>
                        <th>{customFieldsCopy.usedIn}</th>
                        <th>{customFieldsCopy.source}</th>
                        <th>{customFieldsCopy.requiredOnPublish}</th>
                        <th>{customFieldsCopy.visibility}</th>
                        <th>{customFieldsCopy.status}</th>
                        <th>{customFieldsCopy.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mineVisible.map((row) => (
                        <tr key={row.taxonomyId}>
                          <td>{row.name}</td>
                          <td>{fieldTypeLabel(row.fieldType)}</td>
                          <td>{usedInLabel(row.usedIn)}</td>
                          <td>{row.source === "GLOBAL" ? customFieldsCopy.sourceGlobal : customFieldsCopy.sourceStudio}</td>
                          <td>
                            <Label className="d-flex align-items-center gap-2 mb-0">
                              <Input
                                type="switch"
                                checked={row.requiredOnPublish}
                                onChange={(e) => toggleOrg(row, { requiredOnPublish: e.target.checked })}
                              />
                              {row.requiredOnPublish ? "ON" : "OFF"}
                            </Label>
                          </td>
                          <td>
                            <Input
                              type="select"
                              aria-label={`${customFieldsCopy.visibility}: ${row.name}`}
                              value={row.visibility || "INTERNAL"}
                              onChange={(e) => toggleOrg(row, { visibility: e.target.value as any })}
                            >
                              <option value="INTERNAL">{customFieldsCopy.visibilityInternal}</option>
                              <option value="PUBLIC">{customFieldsCopy.visibilityPublic}</option>
                            </Input>
                          </td>
                          <td>{row.enabled ? customFieldsCopy.enabled : customFieldsCopy.disabled}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button size="sm" color="secondary" outline onClick={() => openEdit(row)}>
                                {customFieldsCopy.edit}
                              </Button>
                              {(row.usedIn === "PROCESS" || row.usedIn === "BOTH") && row.enabled ? (
                                <Button size="sm" color="secondary" outline onClick={() => setMappingFieldId(row.taxonomyId)}>
                                  Processes
                                </Button>
                              ) : null}
                              {row.enabled ? (
                                <Button size="sm" color="danger" outline onClick={() => removeFromFuture(row)}>
                                  {customFieldsCopy.removeFromFutureUse}
                                </Button>
                              ) : (
                                <Button size="sm" color="primary" outline onClick={() => toggleOrg(row, { enabled: true })}>
                                  {customFieldsCopy.reEnable}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="hkp-cf-cards">
                  {mineVisible.map((row) => (
                    <div className="hkp-cf-card" key={row.taxonomyId}>
                      <strong>{row.name}</strong>
                      <p className="mb-1">{fieldTypeLabel(row.fieldType)}</p>
                      <p className="text-muted mb-2">
                        {usedInLabel(row.usedIn)} · {row.source === "GLOBAL" ? customFieldsCopy.sourceGlobal : customFieldsCopy.sourceStudio}
                      </p>
                      <Label className="d-flex justify-content-between mb-1">
                        <span>{customFieldsCopy.requiredOnPublish}</span>
                        <Input
                          type="switch"
                          checked={row.requiredOnPublish}
                          onChange={(e) => toggleOrg(row, { requiredOnPublish: e.target.checked })}
                        />
                      </Label>
                      <Label className="d-flex justify-content-between mb-2">
                        <span>{customFieldsCopy.enabled}</span>
                        <Input
                          type="switch"
                          checked={row.enabled}
                          onChange={(e) => (e.target.checked ? toggleOrg(row, { enabled: true }) : removeFromFuture(row))}
                        />
                      </Label>
                      <Label className="d-flex justify-content-between align-items-center mb-2 gap-2">
                        <span>{customFieldsCopy.visibility}</span>
                        <Input
                          type="select"
                          aria-label={`${customFieldsCopy.visibility}: ${row.name}`}
                          value={row.visibility || "INTERNAL"}
                          onChange={(e) => toggleOrg(row, { visibility: e.target.value as any })}
                        >
                          <option value="INTERNAL">{customFieldsCopy.visibilityInternal}</option>
                          <option value="PUBLIC">{customFieldsCopy.visibilityPublic}</option>
                        </Input>
                      </Label>
                      <Button size="sm" color="secondary" outline onClick={() => openEdit(row)}>
                        {customFieldsCopy.edit}
                      </Button>
                      {(row.usedIn === "PROCESS" || row.usedIn === "BOTH") && row.enabled ? (
                        <Button className="ms-2" size="sm" color="secondary" outline onClick={() => setMappingFieldId(row.taxonomyId)}>
                          Processes
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </>
            )
          ) : loading ? (
            <p className="text-muted">Loading custom fields…</p>
          ) : browseVisible.length === 0 ? (
            <div className="hkp-cf-empty">
              <p>{customFieldsCopy.noSearchResults}</p>
              <Button color="primary" size="sm" onClick={() => openAdd(search)}>
                {customFieldsCopy.createNewField}
              </Button>
            </div>
          ) : (
            <div className="hkp-cf-browse">
              {browseVisible.map((row) => {
                const added = enabledIds.has(row.id);
                return (
                  <div className="hkp-cf-browse-row" key={row.id}>
                    <div>
                      <strong>{row.name}</strong>
                      <p className="mb-0 text-muted">
                        {fieldTypeLabel(row.fieldType)} · {usedInLabel(row.usedIn)} · {row.source === "GLOBAL" ? customFieldsCopy.sourceGlobal : customFieldsCopy.sourceStudio}
                      </p>
                    </div>
                    {added ? (
                      <span className="text-muted">{customFieldsCopy.added}</span>
                    ) : (
                      <Button size="sm" color="primary" outline onClick={() => addToStudio(row)}>
                        {customFieldsCopy.addToStudio}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {mappingFieldId ? <div className="hkp-cf-mapping-panel">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>{mine.find((row) => row.taxonomyId === mappingFieldId)?.name}</strong>
              <Button type="button" close aria-label="Close process mappings" onClick={() => setMappingFieldId("")} />
            </div>
            <ProcessFieldMappingEditor taxonomyId={mappingFieldId} />
          </div> : null}
        </CardBody>
      </Card>
      <TaxonomyFieldOverlay
        open={overlay.open}
        mode={overlay.mode}
        initial={overlay.initial}
        definitions={browse}
        saving={saving}
        error={overlayError}
        onClose={() => setOverlay({ open: false, mode: "add" })}
        onSave={handleSave}
        onUseExisting={useExisting}
      />
    </div>
  );
};

export default CustomFields;
